import React from 'react'
import { Grid, Typography, Paper, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { printValue } from '../utils/PrintValue'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  paper: {
    padding: theme.spacing(2),
    color: 'rgba(0,0,0,0.9)',
    height: '100%'
  },
  descript: { fontSize: '0.8rem', color: '#545454' },
  descriptVal: { fontSize: '0.85rem' },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4)
  }
}))

const displayText = v => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string' && (v === 'null' || v === 'undefined')) return ''
  return v
}

const formatCityStateZip = (city, state, zip) => {
  const cityState = [city, state].filter(Boolean).join(', ')
  return [cityState, zip].filter(Boolean).join(' ')
}

const formatPayFrequency = val => {
  if (!val || val === '0' || val === 0) return ''
  if (val === '3' || val === 3) return 'Semi-Monthly'
  return val.toString()
}

export default function ResourceMain(props) {
  const classes = useStyles()
  const { data, isLoading } = props

  const renderValue = (lookup, description, options = {}, secondary = null, secondType = null) => {
    return printValue(data, lookup, description, secondary, secondType, {
      ...options,
      classes: {
        description: classes.descript,
        value: classes.descriptVal
      }
    })
  }

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  const hasData = data && Object.keys(data).length > 0

  if (!hasData) {
    return (
      <div className={classes.loadingContainer}>
        <Typography variant="subtitle1">No data available</Typography>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={1}
        direction='row'
        justifyContent='flex-start'
        alignItems='stretch'
      >
        {/* Row 1: Main Info + Primary Assignment */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Main Info</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='space-evenly'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6}>
                {renderValue('NGIVENNAME', 'First Name')}
                {renderValue('NFAMILYNAME', 'Last Name')}
                {renderValue('NMIDDLENAME', 'Middle Name')}
                {renderValue('NPREFERREDGIVENNAME', 'Preferred Name')}
                {renderValue('HRORGANIZATION', 'HR Org')}
                {renderValue('RELATIONSHIPTOORGANIZATION', 'Relationship to Org')}
                {renderValue('RELATIONSHIPSTATUS', 'Status')}
                {renderValue('WORKTYPE', 'Work Type')}
                {renderValue('ANNIVERSARYDATE', 'Anniversary Date')}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderValue('STARTDATE', 'Hire Date')}
                {renderValue('YEARS_OF_SERVICE', 'Years of Service')}
                {renderValue('ADJUSTEDSTARTDATE', 'Adjusted Hire Date')}
                {renderValue('ADJUSTED_YEARS_OF_SERVICE', 'Adjusted Years of Service')}
                {renderValue('FIRSTDATEWORKED', 'First Date Worked')}
                {renderValue('LASTDATEWORKED', 'Last Date Worked')}
                {renderValue('TERMINATIONNOTICEDATE', 'Term Notice', { type: 'date' })}
                {renderValue('TERMINATIONDATE', 'Term Date')}
                {renderValue('RETIREMENTDATE', 'Retirement Date')}
                {renderValue('LASTDATEPAID', 'Last Date Paid')}
                {renderValue('ELIGIBLE_FOR_REHIRE', 'Elig for Rehire')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Primary Assignment</Typography>
            {renderValue('POSITION', 'Position', {}, 'POSITION_DESCRIPTION')}
            {renderValue('HRORGANIZATIONUNIT', 'HR Org Unit', {}, 'HRORGANIZATIONUNIT_DESCRIPTION')}
            {renderValue('LOCATION', 'Location', {}, 'HRLOCATION_DESCRIPTION')}
            {renderValue('JOB', 'Job', {}, 'JOB_DESCRIPTION')}
            {renderValue('PREFERREDJOBTITLE', 'Title')}
            {renderValue('DIRECT_SUPERVISOR', 'Direct Supervisor')}
            {renderValue('DIRECT_SUPERVISOR_TITLE', 'Direct Supervisor Title')}
            {renderValue('INDIRECT_SUPERVISOR', 'Indirect Supervisor')}
            {renderValue('INDIRECT_SUPERVISOR_TITLE', 'Indirect Supervisor Title')}
            {renderValue('BARGAININGUNIT', 'Bargain Unit')}
            {renderValue('UNION', 'Union Code')}
          </Paper>
        </Grid>

        {/* Row 2: Employee Personal + Address */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Employee Personal</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='space-evenly'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6}>
                {renderValue('BIRTHCITY', 'Birth City')}
                {renderValue('BIRTHCOUNTRY', 'Birth Country')}
                {renderValue('NATIONALITY', 'Nationality')}
                {renderValue('NATIONALITYCOUNTRY', 'Nationality Country')}
                {renderValue('BIRTHDATE', 'Birth Date')}
                {renderValue('AGE', 'Age', { type: 'int' })}
                {renderValue('DECEASED', 'Deceased')}
                {renderValue('DEATHDATE', 'Date of Death')}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderValue('GENDER', 'Gender')}
                {renderValue('ETHNICITY', 'Ethnicity')}
                {renderValue('MARITALSTATUS', 'Marital Status')}
                {renderValue('VETERANCODE', 'Veteran Type')}
                {renderValue('MILITARYRESERVE', 'Military Reserve')}
                {renderValue('DISCHARGEDATE', 'Discharge Date')}
                {renderValue('PROTECTEDVETERANSTATUS', 'Military Status')}
                {renderValue('RELIGION', 'Religion')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Address</Typography>
            <div className={classes.descriptVal}>{displayText(data['PADAADDRESSLINE1'])}</div>
            <div className={classes.descriptVal}>{displayText(data['PADAADDRESSLINE2'])}</div>
            <div className={classes.descriptVal}>{displayText(data['PADAADDRESSLINE3'])}</div>
            <div className={classes.descriptVal}>{displayText(data['PADAADDRESSLINE4'])}</div>
            <div className={classes.descriptVal}>{formatCityStateZip(displayText(data['PAMUNICIPALITY']), displayText(data['PASTATEPROVINCE']), displayText(data['PAPOSTALCODE']))}</div>
            <br />
            <Typography variant='h5'>Supplemental Address</Typography>
            <div className={classes.descriptVal}>{displayText(data['SUPP_PADAADDRESSLINE1'])}</div>
            <div className={classes.descriptVal}>{displayText(data['SUPP_PADAADDRESSLINE2'])}</div>
            <div className={classes.descriptVal}>{displayText(data['SUPP_PADAADDRESSLINE3'])}</div>
            <div className={classes.descriptVal}>{displayText(data['SUPP_PADAADDRESSLINE4'])}</div>
            <div className={classes.descriptVal}>{formatCityStateZip(displayText(data['SUPP_PAMUNICIPALITY']), displayText(data['SUPP_PASTATEPROVINCE']), displayText(data['SUPP_PAPOSTALCODE']))}</div>
          </Paper>
        </Grid>

        {/* Row 3: Contact + Primary Compensation + Work Info */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Contact</Typography>
            {renderValue('WORK_EMAIL', 'Work Email')}
            {renderValue('PERSONAL_EMAIL', 'Personal Email')}
            {renderValue('WORK_PHONE_COUNTRY', 'Work Phone Country')}
            {renderValue('WORK_PHONE_NUMBER', 'Work Phone Number')}
            {renderValue('HOME_PHONE_COUNTRY', 'Home Phone Country')}
            {renderValue('HOME_PHONE_NUMBER', 'Home Phone Number')}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Primary Compensation</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='space-evenly'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6}>
                {renderValue('PAYRATE', 'Hourly', { type: 'decimal', decimals: 2 })}
                {renderValue('ANNUAL_COMP', 'Total Annual', { type: 'decimal', decimals: 2 })}
                {renderValue('PPD_COMP', 'Pay Period', { type: 'decimal', decimals: 2 })}
                {renderValue('MONTHLY_COMP', 'Monthly', { type: 'decimal', decimals: 2 })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderValue('FTE', 'FTE', { type: 'decimal', decimals: 6 })}
                <div>
                  <span style={{ fontSize: '0.8rem', color: '#545454' }}>Pay Frequency:  </span>
                  <span style={{ fontSize: '0.85rem' }}>{formatPayFrequency(data['PAY_FREQUENCY'])}</span>
                </div>
                {renderValue('ANNUALHOURSPER1FTE', 'Annual Hours')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Work Info</Typography>
            {renderValue('SOURCE', 'Hire Source')}
            {renderValue('WORKELIGIBILITYSTATUS', 'Work Elig Status')}
            {renderValue('ASTATEPROVINCE', 'Work State')}
          </Paper>
        </Grid>

        {/* Row 4: Work Eligibility (full width, 3 columns) */}
        <Grid item xs={12}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Work Eligibility</Typography>
            <Grid
              container
              spacing={1}
              direction='row'
              justifyContent='flex-start'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={12} md={4}>
                {renderValue('ALIENNUMBER', 'I9 Alien Number')}
                {renderValue('ADMISSIONNUMBER', 'I9 Admit Number')}
                {renderValue('ALIENEXPIRATION', 'I9 Status Expiration Date')}
                {renderValue('AUTHORIZATION', 'I9 Authorize')}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='subtitle1'>Document 1</Typography>
                {renderValue('WED1WORKELIGIBILITYTYPE', 'I9 Doc Type')}
                {renderValue('WED1WORKELIGIBILITYDESCRIPTION', 'Description')}
                {renderValue('WED1NUMBER', 'Number')}
                {renderValue('WED1EXPIRATION', 'Expiration Date')}
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='subtitle1'>Document 2</Typography>
                {renderValue('WED2ELIGIBILITYTYPE', 'I9 Doc Type')}
                {renderValue('WED2WORKELIGIBILITYDESCRIPTION', 'Description')}
                {renderValue('WED2NUMBER', 'Number')}
                {renderValue('WED2EXPIRATION', 'Expiration Date')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </div>
  )
}
